class ProductDetail extends HTMLElement {
  connectedCallback() {
    this.generation = (this.generation || 0) + 1;
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.track.children];
    this.colorMedia = new Map();
    this.mediaColors = new Map();
    this.querySelectorAll('[data-color-option-id]').forEach((group) => {
      const color = group.dataset.colorOptionId;
      const ids = [...new Set(group.dataset.colorMediaIds.split(',').map(id => id.trim()).filter(Boolean))];
      this.colorMedia.set(color, ids);
      ids.forEach((id) => {
        if (this.mediaColors.has(id) && this.mediaColors.get(id) !== color)
          this.mediaColors.set(id, null);
        else this.mediaColors.set(id, color);
      });
    });
    this.galleryRevision = 0;
    this.galleryIntent = false;
    this.pendingGalleryMedia = null;
    this.lastGalleryMedia = null;
    this.purchase = this.querySelector('[data-product-purchase]');
    // Theme Editor can detach the section while its cart request finishes.
    this.submitting = Boolean(
      this.purchase.querySelector('.product-page__form[data-cart-pending]') ||
      this.purchase.querySelector('[data-purchase-submit][aria-busy="true"]'),
    );
    this.purchase.querySelectorAll('[data-option-id]').forEach((button) => {
      button.disabled = this.submitting;
    });
    this.feedback = this.querySelector('[data-product-feedback]');
    this.capsule = this.querySelector('[data-purchase-capsule]');
    this.quickAdd = this.dataset.context === 'quick-add';
    const mediaTools = this.querySelector('product-media-tools');
    if (mediaTools && !customElements.get('product-media-tools')) void import(mediaTools.dataset.scriptUrl).catch(() => {});
    this.card = this.querySelector('.product-page__purchase');
    this.mediaIndex = 0;
    this.controlsPassed = false;
    this.footerApproaching = false;
    this.setAttribute('data-enhanced', '');
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('submit', this.onSubmit, { signal });
    this.addEventListener('input', () => this.updateQuantity(), { signal });
    this.addEventListener(
      'cart:pending',
      () => {
        this.submitting = true;
        this.feedback.classList.remove('visually-hidden');
        this.purchase.querySelectorAll('[data-option-id]').forEach((button) => {
          button.disabled = true;
        });
        this.updateQuantity();
      },
      { signal },
    );
    this.addEventListener(
      'cart:settled',
      () => {
        this.submitting = false;
        this.purchase.querySelectorAll('[data-option-id]').forEach((button) => {
          button.disabled = false;
        });
        this.updateQuantity();
      },
      { signal },
    );
    this.track.addEventListener(
      'keydown',
      (event) => {
        if (
          event.target !== this.track ||
          !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
        )
          return;
        event.preventDefault();
        const target =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? this.slides.length - 1
              : this.mediaIndex + (event.key === 'ArrowLeft' ? -1 : 1);
        this.goToMedia(target, true, true);
      },
      { signal },
    );
    for (const eventName of ['pointerdown', 'touchstart', 'wheel']) {
      this.track.addEventListener(eventName, () => { this.galleryIntent = true; }, { passive: true, signal });
    }
    this.track.addEventListener('scroll', () => {
      clearTimeout(this.galleryTimer);
      this.galleryTimer = setTimeout(() => {
        if (!this.isConnected || !this.galleryIntent) return;
        const origin = this.slides[0].offsetLeft + this.track.scrollLeft;
        this.mediaIndex = this.slides.reduce((closest, slide, index) =>
          Math.abs(slide.offsetLeft - origin) < Math.abs(this.slides[closest].offsetLeft - origin) ? index : closest, 0);
        this.syncMediaControls();
        this.syncColorFromMedia(this.slides[this.mediaIndex]?.dataset.mediaId);
      }, 140);
    }, { passive: true, signal });
    const reportMediaError = (event) => {
      if (!(event.target instanceof HTMLImageElement)) return;
      const media = event.target.closest('.product-media');
      if (!media) return;
      event.target.hidden = true;
      media.querySelector('.product-media__error').hidden = false;
    };
    this.track.addEventListener('error', reportMediaError, {
      capture: true,
      signal,
    });
    this.track.querySelectorAll('img').forEach((image) => {
      if (image.complete && !image.naturalWidth)
        reportMediaError({ target: image });
    });
    window.addEventListener('pageshow', () => this.restorePurchase(), {
      signal,
    });
    window.addEventListener('resize', () => this.scheduleLayout(), { signal });
    if (!this.quickAdd && this.capsule) this.connectAssistance(signal);
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const disclosure = event.target.closest('details');
        if (disclosure && this.contains(disclosure)) disclosure.open = true;
      },
      { signal },
    );
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
    this.resizeObserver.observe(this.card);
    this.resizeObserver.observe(this.track);
    if (this.capsule && !this.quickAdd) this.resizeObserver.observe(this.capsule);
    this.mediaObserver?.disconnect();
    if ('IntersectionObserver' in window) {
      this.mediaObserver = new IntersectionObserver(
        (entries) => {
          if (this.trackWidth !== this.track.clientWidth) {
            this.scheduleLayout();
            return;
          }
          for (const entry of entries) {
            const visible = entry.isIntersecting && entry.intersectionRatio > 0;
            entry.target.inert = !visible;
            if (!visible) this.pauseMedia(entry.target);
            else this.restoreMedia(entry.target);
            if (entry.intersectionRatio >= 0.6) {
              this.mediaIndex = this.slides.indexOf(entry.target);
              this.syncMediaControls();
            }
          }
        },
        { root: this.track, threshold: [0, 0.6] },
      );
      this.slides.forEach((slide) => this.mediaObserver.observe(slide));
    } else {
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.mediaFrame);
          this.mediaFrame = requestAnimationFrame(() => {
            const origin = this.slides[0].offsetLeft + this.track.scrollLeft;
            this.mediaIndex = this.slides.reduce(
              (closest, slide, index) =>
                Math.abs(slide.offsetLeft - origin) <
                Math.abs(this.slides[closest].offsetLeft - origin)
                  ? index
                  : closest,
              0,
            );
            this.syncMediaControls();
          });
        },
        { passive: true, signal },
      );
    }
    this.updateLayout();
    this.updateQuantity();
    this.syncMediaControls();
  }

  connectAssistance(signal) {
    this.capsule.hidden =
      !('IntersectionObserver' in window) ||
      this.dataset.stickyAssistance === 'false';
    if (document.querySelector('shopify-account')) {
      customElements.whenDefined('shopify-account').then(() => {
        if (this.isConnected) this.observeAccount();
      });
    }
    document.addEventListener('header:geometry', () => this.scheduleLayout(), {
      signal,
    });
    document.addEventListener('privacy:geometry', () => this.scheduleLayout(), {
      signal,
    });
    document.addEventListener('toggle', () => this.updateCapsuleVisibility(), {
      capture: true,
      signal,
    });
    document.addEventListener('close', () => this.updateCapsuleVisibility(), {
      capture: true,
      signal,
    });
    document.addEventListener(
      'shopify:section:load',
      () => this.scheduleLayout(),
      { signal },
    );
    document.addEventListener(
      'shopify:section:unload',
      () => this.scheduleLayout(),
      { signal },
    );
    document.addEventListener(
      'shopify:section:reorder',
      () => this.scheduleLayout(),
      { signal },
    );
    document.addEventListener(
      'focusin',
      () => {
        this.updateCapsuleVisibility();
        this.scheduleFocusVisibility(true);
      },
      { signal },
    );
    document.addEventListener(
      'focusout',
      () =>
        queueMicrotask(() => {
          if (this.isConnected) this.updateCapsuleVisibility();
        }),
      { signal },
    );
  }

  scheduleLayout() {
    cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = requestAnimationFrame(() => {
      if (this.isConnected) this.updateLayout();
    });
  }

  updateLayout() {
    const width = this.track.clientWidth;
    if (width > 0) {
      if (this.trackWidth !== width) this.goToMedia(this.mediaIndex, false);
      this.trackWidth = width;
    }
    if (this.quickAdd) return;
    this.toggleAttribute(
      'data-sticky-eligible',
      matchMedia('(min-width: 64rem)').matches,
    );
    const top = parseFloat(getComputedStyle(this.card).top) || 0;
    this.observeAssistance(top);
  }

  observeAssistance(top = parseFloat(getComputedStyle(this.card).top) || 0) {
    if (this.quickAdd || !this.capsule || !('IntersectionObserver' in window)) return;
    const form = this.purchase.querySelector('.product-page__form');
    const footer = document.querySelector('.store-footer');
    const bottom =
      this.capsule.offsetHeight +
      (parseFloat(getComputedStyle(this.capsule).bottom) || 0);
    if (
      form === this.observedForm &&
      footer === this.observedFooter &&
      top === this.observedTop &&
      bottom === this.observedBottom
    ) {
      this.observeAccount();
      this.updateCapsuleVisibility();
      return;
    }
    this.observedForm = form;
    this.observedFooter = footer;
    this.observedTop = top;
    this.observedBottom = bottom;
    this.purchaseObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.controlsPassed = form.getBoundingClientRect().bottom <= top;
    this.footerApproaching = Boolean(
      footer && footer.getBoundingClientRect().top <= innerHeight + bottom,
    );
    this.purchaseObserver = new IntersectionObserver(
      ([entry]) => {
        this.controlsPassed =
          !entry.isIntersecting &&
          entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? top);
        this.updateCapsuleVisibility();
      },
      { rootMargin: `${-top}px 0px 0px 0px` },
    );
    this.purchaseObserver.observe(form);
    // The measured capsule footprint defines its exit before the footer enters the viewport.
    if (footer) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          this.footerApproaching =
            entry.isIntersecting || entry.boundingClientRect.top <= 0;
          this.updateCapsuleVisibility();
        },
        { rootMargin: `0px 0px ${bottom}px 0px` },
      );
      this.footerObserver.observe(footer);
    }
    this.observeAccount();
    this.updateCapsuleVisibility();
  }

  observeAccount() {
    const root = document.querySelector('shopify-account')?.shadowRoot;
    if (root === this.accountRoot) return;
    this.accountController?.abort();
    this.accountRoot = root;
    if (!root) return;
    this.accountController = new AbortController();
    const options = { capture: true, signal: this.accountController.signal };
    const update = () =>
      queueMicrotask(() => {
        if (this.isConnected) this.updateCapsuleVisibility();
      });
    // Shopify owns authentication; only the native dialog's visible state is read here.
    for (const event of ['beforetoggle', 'toggle', 'close']) {
      root.addEventListener(event, update, options);
    }
  }

  syncCapsule() {
    if (this.quickAdd || !this.capsule) return;
    const action = this.purchase.querySelector('[data-purchase-submit]');
    const button = this.capsule.querySelector('[data-capsule-action]');
    if (!action) return;
    const price = this.purchase.querySelector('.product-page__current-price');
    this.capsule
      .querySelector('[data-capsule-price]')
      .replaceChildren(...(price ? price.cloneNode(true).childNodes : []));
    button.textContent = this.submitting
      ? action.dataset.loadingLabel
      : action.dataset.capsuleLabel;
    button.disabled = Boolean(
      this.selecting ||
      this.submitting ||
      action.dataset.purchaseMode === 'disabled',
    );
    this.observeAssistance();
  }

  updateCapsuleVisibility() {
    if (this.quickAdd || !this.capsule) return;
    const active = document.activeElement;
    const overlayOpen =
      document.documentElement.hasAttribute('data-privacy-overlay') ||
      document.querySelector(
        'dialog[open], .store-header [data-menu-branch][open], .store-header [data-header-search][open]',
      ) ||
      this.accountRoot?.querySelector('dialog[open]');
    // Enlarged text must leave at least three quarters of the usable viewport for reading.
    const fitsViewport =
      this.observedBottom <= (innerHeight - this.observedTop) / 4;
    const visible =
      !this.capsule.hidden &&
      this.capsule.offsetHeight > 0 &&
      fitsViewport &&
      this.controlsPassed &&
      !this.footerApproaching &&
      !overlayOpen;
    const wasVisible = this.capsule.hasAttribute('data-visible');
    this.capsule.toggleAttribute(
      'data-boundary-hidden',
      !fitsViewport || this.footerApproaching || Boolean(overlayOpen),
    );
    this.capsule.toggleAttribute('data-visible', visible);
    this.capsule.inert = !visible;
    this.capsule.setAttribute('aria-hidden', String(!visible));
    const occupied = visible ? this.observedBottom || 0 : 0;
    if (this.publishedOccupied !== occupied) {
      this.publishedOccupied = occupied;
      document.documentElement.style.setProperty(
        '--purchase-occupied',
        `${occupied}px`,
      );
      document.dispatchEvent(new CustomEvent('purchase:geometry'));
    }
    if (
      !visible && wasVisible && this.capsule.contains(active) && !overlayOpen
    ) {
      const target = this.footerApproaching
        ? this.observedFooter?.querySelector('a[href], button:not([disabled])')
        : this.purchase.querySelector('[data-purchase-submit]:not([disabled])');
      (target || document.querySelector('main')).focus({ preventScroll: true });
      this.scheduleFocusVisibility(true);
    } else if (visible && !wasVisible) {
      this.scheduleFocusVisibility();
    }
  }

  scheduleFocusVisibility(requested = false) {
    if (this.quickAdd || !this.capsule) return;
    const active = document.activeElement;
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => {
      if (!this.isConnected || active !== document.activeElement) return;
      // Negative-tabindex page wrappers can receive focus after clicking ordinary text.
      if (
        !active?.matches('a[href], button, input, select, textarea, summary, [tabindex]') ||
        active.tabIndex < 0 ||
        active.disabled ||
        this.capsule.contains(active) ||
        document.querySelector('dialog[open]') ||
        document.documentElement.hasAttribute('data-privacy-overlay')
      ) return;
      if (
        !document.querySelector('main')?.contains(active) &&
        !this.observedFooter?.contains(active)
      ) return;
      const rect = active.getBoundingClientRect();
      if (!requested && (rect.bottom <= 0 || rect.top >= innerHeight)) return;
      const style = getComputedStyle(document.documentElement);
      const top = parseFloat(style.scrollPaddingTop) || this.observedTop || 0;
      const bottom =
        innerHeight -
        (parseFloat(style.scrollPaddingBottom) || this.publishedOccupied || 0);
      let distance = 0;
      if (rect.top < top || rect.height > bottom - top) distance = rect.top - top;
      else if (rect.bottom > bottom) distance = rect.bottom - bottom;
      if (distance) window.scrollBy({ top: distance, behavior: 'instant' });
    });
  }

  activateCapsule() {
    const action = this.purchase.querySelector('[data-purchase-submit]');
    if (this.selecting || this.submitting) return;
    if (action.dataset.purchaseMode === 'add' && !action.disabled)
      action.form.requestSubmit(action);
    else if (action.dataset.purchaseMode === 'options') {
      const groups = [
        ...this.purchase.querySelectorAll('.product-variant-picker__group'),
      ];
      const group =
        groups.find((item) => !item.querySelector('[aria-pressed="true"]')) ||
        groups[0];
      group?.querySelector('button')?.focus({ preventScroll: true });
      group?.scrollIntoView({
        block: 'start',
        behavior: this.motionBehavior(),
      });
    }
  }

  disconnectedCallback() {
    this.generation += 1;
    this.controller?.abort();
    this.request?.abort();
    this.resizeObserver?.disconnect();
    this.mediaObserver?.disconnect();
    this.purchaseObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.accountController?.abort();
    cancelAnimationFrame(this.layoutFrame);
    cancelAnimationFrame(this.mediaFrame);
    cancelAnimationFrame(this.focusFrame);
    clearTimeout(this.requestTimer);
    clearTimeout(this.galleryTimer);
    this.pendingGalleryMedia = null;
    this.observedForm = null;
    this.accountRoot = null;
    if (this.capsule && !this.quickAdd) {
      this.publishedOccupied = 0;
      document.documentElement.style.removeProperty('--purchase-occupied');
      this.capsule.removeAttribute('data-visible');
      this.capsule.inert = true;
      this.capsule.setAttribute('aria-hidden', 'true');
    }
    this.slides.forEach((slide) => {
      slide.inert = false;
      this.pauseMedia(slide);
    });
    if (this.pendingAction) {
      this.pendingAction.action.disabled = this.pendingAction.wasDisabled;
      this.pendingAction = null;
    }
    this.selecting = false;
    this.purchase.removeAttribute('aria-busy');
    this.purchase.querySelectorAll('[data-option-id]').forEach((option) => {
      option.disabled = false;
    });
  }

  updateQuantity() {
    const input = this.purchase.querySelector('[name="quantity"]');
    if (!input) return;
    this.purchase.querySelectorAll('[data-quantity-step]').forEach((button) => {
      button.hidden = false;
      const decrease = Number(button.dataset.quantityStep) < 0;
      button.disabled =
        input.disabled ||
        this.selecting ||
        this.submitting ||
        (decrease
          ? input.valueAsNumber <= Number(input.min)
          : input.max !== '' && input.valueAsNumber >= Number(input.max));
    });
    this.syncCapsule();
  }

  onClick(event) {
    if (event.target.closest('[data-capsule-action]')) this.activateCapsule();
    const thumbnail = event.target.closest('[data-gallery-thumbnail]');
    if (thumbnail && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      this.showMedia(thumbnail.dataset.galleryThumbnail, true);
    }
    const stepper = event.target.closest('[data-quantity-step]');
    if (stepper) {
      const input = this.purchase.querySelector('[name="quantity"]');
      if (Number(stepper.dataset.quantityStep) < 0) input.stepDown();
      else input.stepUp();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  motionBehavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'instant'
      : 'smooth';
  }

  goToMedia(index, animate = true, userInitiated = false) {
    this.mediaIndex = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[this.mediaIndex];
    this.track.scrollTo({
      left: slide.offsetLeft - this.slides[0].offsetLeft,
      behavior: animate ? this.motionBehavior() : 'instant',
    });
    this.syncMediaControls();
    if (userInitiated) {
      this.galleryIntent = true;
      this.syncColorFromMedia(slide.dataset.mediaId);
    }
  }

  showMedia(id, userInitiated = false) {
    const index = this.slides.findIndex(
      (slide) => slide.dataset.mediaId === id,
    );
    if (index >= 0) {
      if (!userInitiated) {
        this.galleryIntent = false;
        this.lastGalleryMedia = null;
      }
      this.goToMedia(index, false, userInitiated);
    }
  }

  syncColorFromMedia(id) {
    if (!id || this.lastGalleryMedia === id) return;
    this.lastGalleryMedia = id;
    this.galleryRevision = (this.galleryRevision || 0) + 1;
    const color = this.mediaColors?.get(id);
    if (!color || this.submitting) return;
    if (this.selecting) {
      this.pendingGalleryMedia = id;
      return;
    }
    const button = this.purchase.querySelector(`[data-option-id="${color}"]`);
    if (!button || button.getAttribute('aria-pressed') === 'true' || button.hasAttribute('formaction')) return;
    const form = button.closest('.product-variant-picker');
    if (form) void this.selectOption(form, button, { fromGallery: true });
  }

  syncMediaControls() {
    this.querySelectorAll('[data-gallery-thumbnail]').forEach(thumbnail => {
      if (thumbnail.dataset.galleryThumbnail === this.slides[this.mediaIndex]?.dataset.mediaId) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });
  }

  pauseMedia(slide) {
    slide.querySelectorAll('video').forEach((video) => video.pause());
    slide.querySelectorAll('iframe[src]').forEach((frame) => {
      frame.dataset.mediaSrc = frame.src;
      frame.removeAttribute('src');
    });
  }

  restoreMedia(slide) {
    slide.querySelectorAll('iframe[data-media-src]').forEach((frame) => {
      frame.src = frame.dataset.mediaSrc;
      delete frame.dataset.mediaSrc;
    });
  }

  onSubmit(event) {
    if (event.target.matches('.product-variant-picker')) {
      const button = event.submitter;
      if (!button || button.hasAttribute('formaction')) return;
      event.preventDefault();
      if (!this.selecting && !this.submitting)
        this.selectOption(event.target, button);
      return;
    }
    if (!event.target.matches('.product-page__form')) return;
    const action = event.target.querySelector('[data-purchase-submit]');
    if (
      this.selecting ||
      this.submitting ||
      action.disabled ||
      !event.target.elements.id?.value
    ) {
      event.preventDefault();
      return;
    }
    const request = new CustomEvent('cart:submit', {
      bubbles: true,
      cancelable: true,
      detail: { form: event.target, submitter: action },
    });
    if (!this.dispatchEvent(request)) {
      event.preventDefault();
      return;
    }
    this.submitting = true;
    this.submitLabel = action.textContent;
    action.textContent = action.dataset.loadingLabel;
    action.disabled = true;
    action.setAttribute('aria-busy', 'true');
    this.purchase.querySelectorAll('[data-option-id]').forEach((button) => {
      button.disabled = true;
    });
    this.updateQuantity();
  }

  restorePurchase() {
    if (!this.submitting || !this.submitLabel) return;
    this.submitting = false;
    const action = this.purchase.querySelector('[data-purchase-submit]');
    action.textContent = this.submitLabel;
    action.disabled = false;
    action.removeAttribute('aria-busy');
    this.purchase.querySelectorAll('[data-option-id]').forEach((button) => {
      button.disabled = false;
    });
    this.updateQuantity();
  }
  async selectOption(form, button, { fromGallery = false } = {}) {
    if (!fromGallery) {
      clearTimeout(this.galleryTimer);
      this.galleryIntent = false;
      this.pendingGalleryMedia = null;
      this.lastGalleryMedia = null;
    }
    const generation = this.generation;
    const galleryRevision = this.galleryRevision;
    const capsuleFocused = Boolean(this.capsule?.contains(document.activeElement));
    const url = new URL(form.action);
    if (url.origin !== location.origin) return;
    url.searchParams.set('option_values', button.value);
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', this.dataset.section);
    const action = this.purchase.querySelector('[data-purchase-submit]');
    const wasDisabled = action.disabled;
    this.pendingAction = { action, wasDisabled };
    const quantity =
      this.purchase.querySelector('[name="quantity"]').valueAsNumber;
    this.selecting = true;
    this.feedback.textContent = '';
    action.disabled = true;
    this.purchase.setAttribute('aria-busy', 'true');
    this.purchase.querySelectorAll('[data-option-id]').forEach((option) => {
      option.disabled = true;
    });
    this.updateQuantity();
    this.request = new AbortController();
    this.requestTimer = setTimeout(() => this.request.abort(), 12000);
    try {
      const response = await fetch(requestUrl, { signal: this.request.signal });
      if (!response.ok) throw new Error('Product render failed');
      // Import only this theme's purchase fragment from the same-origin Shopify render.
      const rendered = new DOMParser().parseFromString(
        await response.text(),
        'text/html',
      );
      const source = rendered.querySelector('product-detail');
      const purchase = source?.querySelector('[data-product-purchase]');
      if (
        !purchase ||
        source.dataset.product !== this.dataset.product ||
        !purchase.querySelector('.product-page__form')
      ) {
        throw new Error('Invalid product render');
      }
      if (!this.isConnected || generation !== this.generation) return;
      const restoreFocus =
        !fromGallery && !capsuleFocused &&
        (document.activeElement === document.body ||
          this.purchase.contains(document.activeElement));
      this.purchase.replaceChildren(...purchase.childNodes);
      this.dispatchEvent(new CustomEvent('product:variant-change'));
      const input = this.purchase.querySelector('[name="quantity"]');
      const minimum = Number(input.min);
      const step = Number(input.step);
      let nextQuantity = Number.isFinite(quantity)
        ? Math.max(
            minimum,
            minimum + Math.floor((quantity - minimum) / step) * step,
          )
        : minimum;
      if (input.max) nextQuantity = Math.min(nextQuantity, Number(input.max));
      input.value = nextQuantity;
      if (!this.quickAdd) history.replaceState(history.state, '', url);
      else this.dispatchEvent(new CustomEvent('product:option-change', {
        bubbles: true,
        detail: { url: url.href },
      }));
      this.dataset.variantMediaMissing = source.dataset.variantMediaMissing || 'false';
      if (!fromGallery && galleryRevision === this.galleryRevision && this.dataset.variantMediaMissing !== 'true') {
        this.dataset.mediaPresentation = source.dataset.mediaPresentation || this.dataset.mediaPresentation;
        this.showMedia(source.dataset.media);
      }
      if (restoreFocus)
        this.purchase
          .querySelector(`[data-option-id="${button.dataset.optionId}"]`)
          ?.focus({ preventScroll: true });
      this.feedback.textContent = [
        this.purchase.querySelector('.product-page__price')?.textContent.trim(),
        this.purchase
          .querySelector('.product-page__availability')
          ?.textContent.trim(),
      ]
        .filter(Boolean)
        .join('. ');
      this.feedback.classList.add('visually-hidden');
    } catch {
      if (this.isConnected && generation === this.generation) {
        action.disabled = wasDisabled;
        this.feedback.classList.remove('visually-hidden');
        this.feedback.textContent = this.dataset.updateError;
      }
    } finally {
      if (generation !== this.generation) return;
      clearTimeout(this.requestTimer);
      this.pendingAction = null;
      this.selecting = false;
      this.purchase.removeAttribute('aria-busy');
      this.purchase.querySelectorAll('[data-option-id]').forEach((option) => {
        option.disabled = false;
      });
      this.updateQuantity();
      if (!fromGallery && document.activeElement === document.body) {
        const target =
          capsuleFocused && this.capsule.hasAttribute('data-visible')
            ? this.capsule.querySelector('[data-capsule-action]')
            : this.purchase.querySelector(
                `[data-option-id="${button.dataset.optionId}"]`,
              );
        target?.focus({ preventScroll: true });
      }
      const pending = this.pendingGalleryMedia;
      this.pendingGalleryMedia = null;
      if (pending) {
        this.lastGalleryMedia = null;
        this.syncColorFromMedia(this.slides[this.mediaIndex]?.dataset.mediaId);
      }
    }
  }
}

if (!customElements.get('product-detail'))
  customElements.define('product-detail', ProductDetail);
